import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { Markdown } from '../../components/Markdown';
import { ErrorNote, SectionTitle, Spinner } from '../../components/ui';

interface Subcategory { id: string; fn: string; category: string; description: string; topics: string | null }
interface SubDetail extends Subcategory { about: string | null; actions: string | null; documentation: string | null }
interface Mapping {
  id: string;
  subcategory_id: string;
  use_case_id: string | null;
  use_case_name: string | null;
  relevance: string;
  evidence_required: string | null;
  status: string;
  notes: string | null;
  fn: string | null;
  description: string | null;
}
interface UseCaseLite { id: string; name: string }

const FNS = ['Govern', 'Map', 'Measure', 'Manage'];
const STATUS_OPTIONS = ['gap', 'evidence_requested', 'evidence_received', 'satisfied', 'not_applicable'];
const STATUS_LABELS: Record<string, string> = {
  gap: 'Gap', evidence_requested: 'Evidence requested', evidence_received: 'Evidence received',
  satisfied: 'Satisfied', not_applicable: 'N/A',
};
const STATUS_COLORS: Record<string, string> = {
  gap: 'border-red-800 bg-red-950/40 text-red-300',
  evidence_requested: 'border-amber-800 bg-amber-950/40 text-amber-300',
  evidence_received: 'border-cyan-800 bg-cyan-950/40 text-cyan-300',
  satisfied: 'border-green-800 bg-green-950/40 text-green-300',
  not_applicable: 'border-slate-700 bg-slate-900 text-slate-500',
};

export function NistTab({ engagementId, reload }: { engagementId: string; reload: () => Promise<void> }) {
  const [mappings, setMappings] = useState<Mapping[] | null>(null);
  const [totals, setTotals] = useState<Record<string, number>>({});
  const [subs, setSubs] = useState<Subcategory[]>([]);
  const [useCases, setUseCases] = useState<UseCaseLite[]>([]);
  const [fn, setFn] = useState('Govern');
  const [q, setQ] = useState('');
  const [scope, setScope] = useState('');
  const [detail, setDetail] = useState<SubDetail | null>(null);
  const [error, setError] = useState('');

  async function loadMappings() {
    const r = await api.get<{ mappings: Mapping[]; totalsByFn: Record<string, number> }>(`/api/engagements/${engagementId}/nist`);
    setMappings(r.mappings);
    setTotals(r.totalsByFn);
  }
  async function loadSubs() {
    const qs = new URLSearchParams();
    if (fn) qs.set('fn', fn);
    if (q) qs.set('q', q);
    const r = await api.get<{ subcategories: Subcategory[] }>(`/api/nist?${qs.toString()}`);
    setSubs(r.subcategories);
  }

  useEffect(() => {
    void loadMappings().catch(() => setError('Failed to load mappings.'));
    api.get<{ useCases: UseCaseLite[] }>(`/api/engagements/${engagementId}/use-cases`)
      .then((r) => setUseCases(r.useCases))
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);
  useEffect(() => { void loadSubs().catch(() => undefined); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [fn]);

  async function addMapping(sub: Subcategory) {
    setError('');
    try {
      await api.post(`/api/engagements/${engagementId}/nist`, {
        subcategory_id: sub.id,
        use_case_id: scope || null,
      });
      await loadMappings();
      await reload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setError(`${sub.id} is already mapped at that scope.`);
      else setError('Mapping failed.');
    }
  }

  async function patchMapping(m: Mapping, fields: Record<string, unknown>) {
    await api.patch(`/api/nist-mappings/${m.id}`, fields);
    await loadMappings();
  }

  async function removeMapping(m: Mapping) {
    await api.del(`/api/nist-mappings/${m.id}`);
    await loadMappings();
    await reload();
  }

  async function openDetail(id: string) {
    const r = await api.get<{ subcategory: SubDetail }>(`/api/nist/detail/${encodeURIComponent(id)}`);
    setDetail(r.subcategory);
  }

  if (!mappings) return <Spinner />;

  const mappedIds = new Set(mappings.map((m) => `${m.subcategory_id}|${m.use_case_id ?? ''}`));
  const coverage = FNS.map((f) => {
    const inScope = mappings.filter((m) => m.fn === f);
    const satisfied = inScope.filter((m) => m.status === 'satisfied' || m.status === 'not_applicable').length;
    return { f, mapped: inScope.length, satisfied, total: totals[f] ?? 0 };
  });

  return (
    <div>
      {detail && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/80 p-4" onClick={() => setDetail(null)}>
          <div className="max-h-[85vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-300">{detail.id}</h2>
              <button className="btn-secondary !px-3 !py-1 text-xs" onClick={() => setDetail(null)}>Close ✕</button>
            </div>
            <p className="text-sm font-semibold text-slate-200">{detail.description}</p>
            {detail.about && (<><h3 className="mt-4 text-sm font-bold text-slate-100">About</h3><Markdown content={detail.about} /></>)}
            {detail.actions && (<><h3 className="mt-4 text-sm font-bold text-slate-100">Suggested actions</h3><Markdown content={detail.actions} /></>)}
            {detail.documentation && (<><h3 className="mt-4 text-sm font-bold text-slate-100">Documentation prompts</h3><Markdown content={detail.documentation} /></>)}
          </div>
        </div>
      )}

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {coverage.map(({ f, mapped, satisfied, total }) => (
          <div key={f} className="card !p-3 text-center">
            <div className="text-sm font-bold text-slate-100">{f}</div>
            <div className="text-xs text-slate-500">{mapped} mapped / {total} · {satisfied} closed</div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-800">
              <div className="h-full bg-brand-500" style={{ width: total > 0 ? `${(mapped / total) * 100}%` : '0%' }} />
            </div>
          </div>
        ))}
      </div>

      {error && <ErrorNote message={error} />}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="card">
          <SectionTitle>Browse playbook ({subs.length})</SectionTitle>
          <div className="mb-3 flex gap-2">
            <select className="input !w-32" value={fn} onChange={(e) => setFn(e.target.value)}>
              {FNS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <input
              className="input flex-1"
              placeholder="Search id, description, topics…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void loadSubs(); }}
            />
            <select className="input !w-44" value={scope} onChange={(e) => setScope(e.target.value)} title="Mapping scope">
              <option value="">Org-level scope</option>
              {useCases.map((u) => <option key={u.id} value={u.id}>UC: {u.name.slice(0, 24)}</option>)}
            </select>
          </div>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
            {subs.map((s) => {
              const mapped = mappedIds.has(`${s.id}|${scope}`);
              return (
                <div key={s.id} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <button className="font-mono text-sm font-bold text-brand-300 hover:underline" onClick={() => void openDetail(s.id)}>
                      {s.id}
                    </button>
                    <button
                      className={`btn-secondary !px-2.5 !py-1 text-xs ${mapped ? 'opacity-40' : ''}`}
                      disabled={mapped}
                      onClick={() => void addMapping(s)}
                    >
                      {mapped ? 'Mapped ✓' : '+ Map'}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-slate-400">{s.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card">
          <SectionTitle>Engagement mappings ({mappings.length})</SectionTitle>
          {mappings.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">
              Nothing mapped yet. Map controls from the browser, or use “Seed NIST gaps” on the
              Assessment tab to bootstrap from weak dimensions.
            </p>
          ) : (
            <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
              {mappings.map((m) => (
                <div key={m.id} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <button className="font-mono text-sm font-bold text-brand-300 hover:underline" onClick={() => void openDetail(m.subcategory_id)}>
                      {m.subcategory_id}
                    </button>
                    <div className="flex items-center gap-2">
                      <span className={`chip ${STATUS_COLORS[m.status] ?? ''}`}>{STATUS_LABELS[m.status] ?? m.status}</span>
                      <button className="text-xs text-slate-600 hover:text-red-400" onClick={() => void removeMapping(m)}>✕</button>
                    </div>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {m.use_case_name ? `Use case: ${m.use_case_name}` : 'Organization-level'}
                    {m.notes ? ` · ${m.notes}` : ''}
                  </p>
                  <div className="mt-2 flex gap-2">
                    <select className="input !w-44 !py-1 text-xs" value={m.status} onChange={(e) => void patchMapping(m, { status: e.target.value })}>
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                    <input
                      className="input flex-1 !py-1 text-xs"
                      placeholder="Evidence required (e.g. AI policy doc, inventory export)"
                      defaultValue={m.evidence_required ?? ''}
                      onBlur={(e) => { if (e.target.value !== (m.evidence_required ?? '')) void patchMapping(m, { evidence_required: e.target.value }); }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
